import {notNullOrEmpty} from 'common/utils';
import {ScreenReaderUtils} from 'common/utils/screenreader-utils';
import {StringUtils} from 'common/utils/string-utils';
import {Identifiseringsmaate} from 'features/rettsstiftelse/constants/identifiseringsmate';
import {
  Formuesgode as FormuesgodeModel,
  EntydigIdentifisert as EntydigIdentifisertModel,
  SaerskiltIdentifisertStrukturertAksjer as SaerskiltIdentifisertStrukturertAksjerModel,
  SaerskiltIdentifisert as SaerskiltIdentifisertModel,
  Tingsinnbegrep as TingsinnbegrepModel,
  SaerskiltIdentifisertStrukturertMAAID as SaerskiltIdentifisertStrukturertMAAIDModel,
} from 'features/rettsstiftelse/model/losore/formuesgoder';
import {FC} from 'react';
import {createUseStyles} from 'react-jss';
import {useTranslation} from 'react-i18next';

const useFormuesgodeDetailRowStyles = createUseStyles({
  typeBeskrivelse: {
    fontWeight: 600,
    fontStyle: 'normal',
  },
});

const TypeBeskrivelse = ({formuesgode, noIdent}: {formuesgode: FormuesgodeModel; noIdent?: boolean}) => {
  const formuesgodeDetailRowStyles = useFormuesgodeDetailRowStyles();

  return (
    <span className={formuesgodeDetailRowStyles.typeBeskrivelse}>
      {StringUtils.capitalize(formuesgode.typeBeskrivelse)}
      {noIdent ? '' : ': '}
    </span>
  );
};

const useEntydigIdentifisertStyles = createUseStyles({
  historiskRegistreringsnummerList: {
    listStyleType: 'none',
    marginLeft: '0',
    padding: 'unset',
  },
  historiskRegistreringsnummer: {
    display: 'inline',
    margin: '0',
    whiteSpace: 'break-spaces',
    '&:after': {
      content: '", "',
    },
    '&:last-child:after': {
      content: '""',
    },
  },
});

const EntydigIdentifisert: FC<{formuesgode: EntydigIdentifisertModel}> = ({formuesgode}) => {
  const {t} = useTranslation(['rettsstiftelser']);
  const styles = useEntydigIdentifisertStyles();

  let historiskRegistreringsnummer = null;
  if (formuesgode?.historiskRegistreringsnummerMotorvogn) {
    historiskRegistreringsnummer = formuesgode?.historiskRegistreringsnummerMotorvogn?.length > 0;
  }
  const motorvognRegistrert = formuesgode.registreringsnummerMotorvogn as string;

  return (
    <>
      <TypeBeskrivelse formuesgode={formuesgode} />
      {StringUtils.formatRegnr(motorvognRegistrert)}
      {historiskRegistreringsnummer && motorvognRegistrert && (
        <div>
          {t('rettsstiftelser:formuesgoder.tidligereRegnr', {
            count: formuesgode.historiskRegistreringsnummerMotorvogn.length,
          })}
          :
          <ul className={styles.historiskRegistreringsnummerList}>
            {formuesgode.historiskRegistreringsnummerMotorvogn?.map((historiskRegnr, i) => (
              <li key={i} className={styles.historiskRegistreringsnummer}>
                {StringUtils.formatRegnr(historiskRegnr as string)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

const SaerskiltIdentifisert: FC<{formuesgode: SaerskiltIdentifisertModel}> = ({formuesgode}) => {
  if (formuesgode.identifiseringstype === 'identifiseringstype.generellBeskrivelse') {
    return (
      <>
        <TypeBeskrivelse formuesgode={formuesgode} />
        {formuesgode.identifikator}
      </>
    );
  } else if (formuesgode.identifiseringstype === 'identifiseringstype.ingenBeskrivelse') {
    return <TypeBeskrivelse formuesgode={formuesgode} noIdent />;
  } else {
    return (
      <>
        <TypeBeskrivelse formuesgode={formuesgode} />
        {formuesgode.identifiseringstypeBeskrivelse}
        {formuesgode.identifikator ? <div>{formuesgode.identifikator}</div> : null}
      </>
    );
  }
};

const Tingsinnbegrep: FC<{formuesgode: TingsinnbegrepModel}> = ({formuesgode}) => {
  const {t} = useTranslation(['rettsstiftelser']);
  return (
    <>
      <TypeBeskrivelse formuesgode={formuesgode} />
      {StringUtils.capitalize(formuesgode.avgrensingTingsinnbegrepBeskrivelse)}
      {[
        formuesgode.beskrivelseAvgrensing,
        formuesgode.avtaletypeFordringBeskrivelse
          ? `${t('rettsstiftelser:formuesgoder.avtaleTypeLabel', {avtale: formuesgode.avtaletypeFordringBeskrivelse})}`
          : '',
      ]
        .filter(notNullOrEmpty)
        .map((line, i) => (
          <div key={i}>{StringUtils.capitalize(line)}</div>
        ))}
    </>
  );
};

const SaerskiltIdentifisertStrukturertMAAID: FC<{formuesgode: SaerskiltIdentifisertStrukturertMAAIDModel}> = ({
  formuesgode,
}) => (
  <>
    <TypeBeskrivelse formuesgode={formuesgode} />
    {[formuesgode.uregistrertMotorvognMerke, formuesgode.uregistrertMotorvognAarsmodell].filter(s => s).join(', ')}
    {[formuesgode.uregistrertMotorvognIdentifikasjonsnummer, ...(formuesgode.beskrivelse?.split('\n') ?? [])]
      .filter(notNullOrEmpty)
      .map((line, i) => (
        <div key={i}>{StringUtils.capitalize(line)}</div>
      ))}
  </>
);

const SaerskiltIdentifsertStrukturertAkjser: FC<{formuesgode: SaerskiltIdentifisertStrukturertAksjerModel}> = ({
  formuesgode,
}) => {
  const {t} = useTranslation(['rettsstiftelser']);

  return (
    <>
      <TypeBeskrivelse formuesgode={formuesgode} />
      {[
        t('rettsstiftelser:formuesgoder.aksjerI', {
          orgnr: ScreenReaderUtils.setScreenreaderText(formuesgode.organisasjonsnummer),
        }),
        formuesgode.antallAksjer
          ? t('rettsstiftelser:formuesgoder.antallAksjer', {antallAksjer: formuesgode.antallAksjer})
          : null,
        formuesgode.aksjeklasseBeskrivelse,
      ]
        .filter(s => s)
        .join(', ')}
    </>
  );
};

export const Formuesgode: FC<{fg: FormuesgodeModel}> = ({fg}) => {
  switch (fg.identifiseringsmate) {
    case Identifiseringsmaate.ENTYDIG_IDENTIFISERT:
      return <EntydigIdentifisert formuesgode={fg} />;
    case Identifiseringsmaate.SAERSKILT_IDENTIFISERT:
      return <SaerskiltIdentifisert formuesgode={fg} />;
    case Identifiseringsmaate.TINGSINNBEGREP:
      return <Tingsinnbegrep formuesgode={fg} />;
    case Identifiseringsmaate.SAERSKILT_IDENTIFISERT_STRUKTURERT_MAAID:
      return <SaerskiltIdentifisertStrukturertMAAID formuesgode={fg} />;
    case Identifiseringsmaate.SAERSKILT_IDENTIFISERT_STRUKTURERT_AKSJER:
      return <SaerskiltIdentifsertStrukturertAkjser formuesgode={fg} />;
    default:
      return null;
  }
};
